// One deal on the board (spec 09 §4.14 "Board"): the title, the customer, the
// value for a value reader, the close month (red once it has passed, with the
// words beside the colour), the idle chip when the deal rots, and the next
// step or the flag with Schedule. It carries the deal's Peek button and its
// Move button, the one way to every stage on a phone and for a reader who
// cannot drag.
//
// The card is presentational. The board owns the drag: it passes the pointer
// and key handlers in and says whether this card is lifted, pending or the
// roving focus stop. A press on one of the card's own controls never starts a
// drag (`isInteractive`), so the title link, Peek, Move and Schedule stay
// ordinary controls.

import type { KeyboardEvent, PointerEvent } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { RefLabel } from "../../../../../components/ref-label";
import { SIDE_PEEK_ID } from "../../../../../components/peek-address";
import { formatMinor } from "../../lib/money";
import { formatMonth } from "../../lib/pipeline";
import type { Opportunity } from "../../hooks/opportunity-queries";
import { IdleChip, NextStepCell } from "./pipeline-parts";

/** Is the pointer's target one of the card's own controls? */
export function isInteractive(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest("a, button, input, select, textarea") !== null;
}

export interface BoardCardPeek {
  readonly code: string | undefined;
  readonly current: string | undefined;
  readonly open: (code: string) => void;
  readonly close: () => void;
}

export function BoardCard({
  deal,
  pending,
  moveBlocked = false,
  lifted,
  focusable,
  peek,
  canMove,
  onMove,
  onSchedule,
  onPointerDown,
  onKeyDown,
  onFocus,
}: {
  readonly deal: Opportunity;
  readonly pending: boolean;
  /** Another card's move is saving or waiting for its reason, and the board
   * starts one move at a time, so Move waits too. */
  readonly moveBlocked?: boolean | undefined;
  readonly lifted: boolean;
  /** The board's roving focus stop. */
  readonly focusable: boolean;
  readonly peek: BoardCardPeek | undefined;
  readonly canMove: boolean;
  readonly onMove: () => void;
  readonly onSchedule: (() => void) | undefined;
  readonly onPointerDown: (event: PointerEvent<HTMLLIElement>) => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLLIElement>) => void;
  readonly onFocus: () => void;
}) {
  const { t, i18n } = useTranslation();
  const code = peek?.code;
  const peeked = code !== undefined && peek?.current === code;
  const classes = ["sales-board-card"];
  if (lifted) classes.push("is-lifted");
  if (pending) classes.push("is-pending");
  if (peeked) classes.push("is-peeked");
  return (
    <li
      className={classes.join(" ")}
      tabIndex={focusable ? 0 : -1}
      aria-label={deal.title}
      aria-roledescription={t("sales.pipeline.board.move")}
      aria-busy={pending || undefined}
      data-testid="sales-board-card"
      data-deal-id={deal.id}
      data-stage-id={deal.stage_id}
      data-pending={pending ? "true" : "false"}
      data-rotting={deal.rotting ? "true" : "false"}
      data-peek-row={code}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
    >
      <div className="sales-board-card-head">
        <Link
          to="/sales/opportunities/$opportunityId"
          params={{ opportunityId: deal.id }}
          className="ds-lnk sales-board-card-title"
          data-testid="sales-board-card-title"
        >
          {deal.title}
        </Link>
        {peek !== undefined && code !== undefined ? (
          <button
            type="button"
            className="ds-peek-btn"
            data-testid="ds-peek"
            data-peek-key={code}
            aria-expanded={peeked}
            {...(peeked ? { "aria-controls": SIDE_PEEK_ID } : {})}
            aria-label={t("sales.peek.openNamed", { name: deal.title })}
            onClick={(event) => {
              // The peeked card's button closes it, as a table row's does.
              event.currentTarget.focus();
              if (peeked) peek.close();
              else peek.open(code);
            }}
          >
            {t("sales.peek.open")}
          </button>
        ) : null}
      </div>
      <div className="sales-board-card-customer" data-testid="sales-board-card-customer">
        <RefLabel value={deal.customer_name ?? undefined} id={deal.customer_id} />
      </div>
      <div className="sales-board-card-facts">
        {deal.expected_value_minor === undefined ? null : deal.expected_value_minor === null ? (
          <span className="sales-hint" data-testid="sales-board-card-value" data-valued="false">
            {t("sales.pipeline.noValue")}
          </span>
        ) : (
          <span className="num" data-testid="sales-board-card-value" data-valued="true">
            {formatMinor(deal.expected_value_minor, deal.currency, i18n.language)}
          </span>
        )}
        <span
          className={deal.close_month_passed ? "sales-valid-urgent" : "sales-hint"}
          data-testid="sales-board-card-close"
          data-passed={deal.close_month_passed ? "true" : "false"}
        >
          {t("sales.pipeline.closeMonth", {
            month: formatMonth(deal.expected_close_month, i18n.language),
          })}
          {deal.close_month_passed ? (
            <span className="visually-hidden"> {t("sales.pipeline.closeMonthPassed")}</span>
          ) : null}
        </span>
        {deal.rotting ? <IdleChip days={deal.idle_days} rotting /> : null}
      </div>
      <div className="sales-board-card-foot">
        <NextStepCell
          next={deal.next_activity}
          needsNextStep={deal.needs_next_step}
          onSchedule={onSchedule}
          scheduleName={deal.title}
        />
        {canMove ? (
          <Button
            size="sm"
            data-testid="sales-board-card-move"
            aria-label={t("sales.pipeline.board.moveAria", { title: deal.title })}
            disabled={pending || moveBlocked}
            onClick={onMove}
          >
            {t("sales.pipeline.board.move")}
          </Button>
        ) : null}
      </div>
      {pending ? (
        <p className="sales-hint" data-testid="sales-board-card-pending">
          {t("sales.pipeline.board.pending")}
        </p>
      ) : null}
    </li>
  );
}
